<template>
  <div v-if="loading">
    <p-o-s-page-skeleton variant="table" />
  </div>
  <div v-else-if="!hasPermission">
    <p-o-s-access-denied />
  </div>
  <div v-else>
    <div class="mb-3">
      <!-- Row 1: type tabs (scrollable) -->
      <div class="zone-wrapper">
        <ul class="nav seg-filter flex-nowrap">
          <li
            class="cursor-pointer nav-item"
            @click="() => {
              if (params.type === '') return;
              params.type = '';
              fetchData();
            }"
          >
            <a class="nav-link" :class="{ 'active': params.type === '' }">
              {{ $t('All') }}
            </a>
          </li>
          <li
            v-for="t in sessionTypes"
            :key="t.value"
            class="cursor-pointer nav-item"
            @click="() => {
              if (params.type === t.value) return;
              params.type = t.value;
              fetchData();
            }"
          >
            <a class="nav-link" :class="{ 'active': params.type === t.value }">
              <i :class="t.icon" class="me-1"/>{{ $t(t.label) }}
            </a>
          </li>
        </ul>
      </div>

      <!-- Row 2 (normal): active-count stat (left) + actions (right; full-width row on phones) -->
      <div v-if="!selectMode" class="session-bar mt-2">
        <span class="pos-stat bg-success-subtle text-success-emphasis">
          <span class="dot bg-success"/>
          {{ sessions.length }} {{ $t('Active') }}
        </span>
        <div class="session-bar__btns">
          <button
            v-if="canOpenDrawer"
            type="button"
            class="btn btn-sm btn-outline-secondary fw-bold rounded-3"
            :disabled="openingDrawer"
            @click="handleOpenDrawer"
          >
            <i :class="openingDrawer ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-cash-register'" class="me-2"/>{{ $t('Open Drawer') }}
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-theme fw-bold rounded-3"
            :disabled="!printableItems.length"
            @click="enterSelectMode"
          >
            <i class="fa-solid fa-square-check me-2"/>{{ $t('Select') }}
          </button>
          <button class="btn btn-sm btn-theme fw-bold rounded-3" @click="openNewSessionModal">
            <i class="fa-solid fa-plus me-2"/>{{ $t('New Session') }}
          </button>
        </div>
      </div>

      <!-- Row 2 (select mode): bulk-actions toolbar, full width -->
      <bulk-print-qr-toolbar
        v-else
        class="mt-2"
        :count="selectedIds.length"
        :total="printableItems.length"
        :all-selected="allSelectedQr"
        :qr-count="selectedIds.length"
        :close-count="selectedIds.length"
        :show-open="false"
        :show-print-qr="$hasFeature('feature_qr_ordering')"
        :busy="busy"
        :busy-label="busyLabel"
        @toggle-all="toggleAllQr"
        @print-qr="printSelectedQr"
        @close="closeSelected"
        @cancel="exitSelectMode"
      />
    </div>

    <div class="table-container" v-if="sessions.length > 0">
      <div
        v-for="session in sessions"
        :key="session.id"
        class="session-card position-relative"
        :class="[
          session.activated_at ? 'status-occupied' : 'status-waiting',
          {
            'qr-select-on': selectMode && isPrintable(session),
            'qr-selected': selectMode && selectedIds.includes(session.id),
            'qr-select-dim': selectMode && !isPrintable(session),
          }
        ]"
        @click="onCardClick(session)"
      >
        <span class="sc-rail"/>

        <!-- HEAD: ซ้าย = #เลข + ประเภท/โต๊ะ · ขวา = ยอดเงิน (พระเอก) / QR -->
        <div class="sc-head">
          <div class="sc-id">
            <span class="sc-num">#{{ displayOrderNo(session) }}</span>
            <div class="sc-meta">
              <span class="sc-type" :class="'sc-type--' + session.type">
                <i :class="typeIcon(session.type)" class="me-1"/>{{ $t(typeLabel(session.type)) }}
              </span>
              <span v-if="session.type === 'dine_in'" class="sc-chip">
                <i class="fa-solid fa-chair me-1"/>{{ session.table_name || $t('No table') }}
              </span>
              <span v-if="session.activated_at" class="sc-chip">
                <i class="fa-regular fa-clock me-1"/>{{ $storeIndex.getTimeDiff(session.activated_at) }}
              </span>
              <span
                v-if="session.is_request"
                class="sc-alert sc-alert--call"
                :title="$t('Customer is calling staff')"
              >
                <i class="fa-solid fa-bell me-1"/>{{ $t('Calling') }}
              </span>
              <span
                v-if="session.is_bill_requested"
                class="sc-alert sc-alert--bill"
                :title="$t('Customer requested the bill')"
              >
                <i class="fa-solid fa-file-invoice-dollar me-1"/>{{ $t('Bill') }}
              </span>
            </div>
          </div>
          <div class="sc-right">
            <template v-if="session.activated_at">
              <div class="sc-amount">{{ $storeIndex.formatThaiCurrency(session.total || 0) }}</div>
              <div class="sc-items">
                <i class="fa-solid fa-bowl-food me-1"/>{{ session.items_count || 0 }} {{ $t('items') }}
              </div>
            </template>
            <!-- ยังไม่มีออเดอร์ — แพ็กไม่มี QR ใช้ป้าย "รอสั่ง" แทน "QR พร้อม" (ดู pos/tables/index.vue) -->
            <div v-else class="sc-qr">
              <template v-if="qrOrdering"><span class="sc-qr__dot"/>{{ $t('QR Ready') }}</template>
              <template v-else><i class="fa-solid fa-bell-concierge"/>{{ $t('Waiting for order') }}</template>
            </div>
          </div>
        </div>

        <div class="sc-divider"/>

        <!-- FOOT: พนักงาน · ลูกค้า (แถวเดียว ไม่ตกบรรทัด) -->
        <div class="sc-foot">
          <span class="sc-staff">
            <i class="fa-solid fa-user-tie me-1"/>{{ session.service_staff || $t('Unassigned') }}
          </span>
          <span class="sc-foot-item">
            <i class="fa-solid fa-user"/>{{ session.customer_name || $t('Walk-in') }}
          </span>
        </div>
      </div>
    </div>
    <div v-else class="text-center text-muted py-5">
      <i class="fa-solid fa-receipt fa-3x mb-3 opacity-50"/>
      <p class="fw-bold mb-3">{{ $t('No Available Data') }}</p>
      <button class="btn btn-theme text-white fw-bold" @click="openNewSessionModal">
        <i class="fa-solid fa-plus me-2"/>{{ $t('Create first session') }}
      </button>
    </div>
  </div>

  <modal-new-session
    v-if="newSessionModal.open"
    @close="newSessionModal.open = false"
    @saved="handleSessionCreated"
  />

  <session-actions-modal
    v-if="actionsModal.open"
    variant="session"
    :session="actionsModal.session"
    :order-no="actionsModal.session ? displayOrderNo(actionsModal.session) : ''"
    @close="actionsModal.open = false"
    @check-bill="openCheckBill"
    @move-table="openMoveTable"
    @merge-session="openMergeSession"
    @manage-requests="openManageRequests"
    @closed="onSessionClosed"
  />

  <modal-service-requests
    v-if="serviceRequests.open"
    :token="serviceRequests.token"
    :name="serviceRequests.name"
    @close="() => {
      serviceRequests.open = false;
      serviceRequests.token = '';
    }"
    @changed="fetchData"
  />

  <offcanvas-check-bill
    v-if="checkBill.open"
    :session-name="checkBill.name"
    :session-token="checkBill.token"
    @close="checkBill.open = false"
  />

  <offcanvas-move-table
    v-if="moveTable.open"
    :token="moveTable.token"
    :table-id="moveTable.id"
    :table-name="moveTable.name"
    :table-total="moveTable.total"
    @close="moveTable.open = false"
    @moved="onSessionMoved"
  />

  <offcanvas-merge-session
    v-if="mergeSession.open"
    :host-token="mergeSession.hostToken"
    @close="mergeSession.open = false"
    @merged="onSessionsMerged"
  />

</template>

<script>
import POSPageSkeleton from '@/plugins/POSPageSkeleton.vue';
import POSAccessDenied from '@/plugins/POSAccessDenied.vue';
import ModalNewSession from './ModalNewSession.vue';
import SessionActionsModal from './SessionActionsModal.vue';
import ModalServiceRequests from '@/components/pos/tables/ModalServiceRequests.vue';
import OffcanvasCheckBill from '@/components/shared/billing/OffcanvasCheckBill.vue';
import OffcanvasMoveTable from '@/components/pos/tables/OffcanvasMoveTable.vue';
import OffcanvasMergeSession from '@/components/pos/tables/OffcanvasMergeSession.vue';
import BulkPrintQrToolbar from '@/components/pos/BulkPrintQrToolbar.vue';
import { Sessions } from '@/services/endpoint/pos.service';
import { isSupported as escposIsSupported, openCashDrawer } from '@/utils/escposPrinter';
import { hasCashDrawer } from '@/utils/printerSettings';
import { printQrSlips, serviceUrl } from '@/utils/qrPrint';
import { usePOSBranchSocket } from '@/composables/usePOSBranchSocket';
import { useShopStore } from '@/stores/shop.store';
import { playNotifySound, NOTIFY_SOUNDS } from '@/composables/useNotifySound';

export default {
  name: 'POSSessions',
  components: {
    POSPageSkeleton,
    POSAccessDenied,
    ModalNewSession,
    SessionActionsModal,
    ModalServiceRequests,
    OffcanvasCheckBill,
    OffcanvasMoveTable,
    OffcanvasMergeSession,
    BulkPrintQrToolbar
  },
  data() {
    return {
      // channel=store — ออเดอร์จาก delivery platform ภายนอกแยกไปหน้า "ออเดอร์ Platform"
      params: { type: '', channel: 'store' },
      sessions: [],
      loading: false,
      openingDrawer: false,
      hasPermission: false,
      sessionTypes: [
        { value: 'dine_in',  label: 'Dine-in',  icon: 'fa-solid fa-utensils' },
        { value: 'takeaway', label: 'Takeaway', icon: 'fa-solid fa-bag-shopping' },
        { value: 'delivery', label: 'Delivery', icon: 'fa-solid fa-motorcycle' }
      ],
      newSessionModal: { open: false },
      actionsModal:    { open: false, session: null },
      serviceRequests: { open: false, token: '', name: '' },
      checkBill:       { open: false, name: '', token: '' },
      moveTable:       { open: false, token: '', id: 0, name: '', total: 0 },
      mergeSession:    { open: false, hostToken: '' },
      // In-page "select & bulk actions" mode (toggled from the header button).
      selectMode:      false,
      selectedIds:     [],
      printing:        false,
      printedCount:    0,
      closing:         false
    };
  },
  async mounted() {
    const { branchId } = useShopStore();

    usePOSBranchSocket({
      branchId,
      onRequested: this.websocketRequested,
      onBillRequested: this.websocketBillRequested,
      onOpen: this.websocketSessionOpen,
      onClose: this.websocketSessionClose,
    });

    this.fetchData();
  },
  computed: {
    // แพ็กมีสั่งผ่าน QR ไหม — ไม่มี = สถานะ qr_ready (เปิดแล้วยังไม่สั่ง) ขึ้นป้าย "รอสั่ง" แทน "QR พร้อม"
    qrOrdering() {
      return this.$hasFeature('feature_qr_ordering');
    },
    // "Open Drawer" only on a device whose printer has a drawer cabled to it
    // (printer settings) — a portable printer or a waiter's phone never shows it.
    canOpenDrawer() {
      return escposIsSupported() && hasCashDrawer();
    },
    // Sessions ready for bulk QR printing (any active session has a token). Slip
    // heading = table name when the session sits on a table, else its session no.
    printableItems() {
      return (this.sessions || [])
        .filter(s => s.token)
        .map(s => {
          const no = this.displayOrderNo(s);
          return {
            id: s.id,
            name: s.table_name || ('#' + no),
            heading: s.table_name
              ? this.$t('Table') + ' ' + s.table_name
              : this.$t('Session') + ' #' + no,
            token: s.token,
            badgeText: this.$t(this.typeLabel(s.type)),
            badgeClass: s.type === 'dine_in' ? 'bg-primary' : 'bg-secondary',
          };
        });
    },
    printableIdSet() {
      return new Set(this.printableItems.map(it => it.id));
    },
    allSelectedQr() {
      return this.printableItems.length > 0 && this.selectedIds.length === this.printableItems.length;
    },
    busy() {
      return this.printing || this.closing;
    },
    busyLabel() {
      if (this.printing) return this.$t('Printing') + ' ' + this.printedCount + '/' + this.selectedIds.length;
      if (this.closing) return this.$t('Processing');
      return '';
    }
  },
  methods: {
    // No Sale — open the cash drawer without a transaction (wrong change, coin
    // swap). Button only shows on a device set up with a drawer; if the paired
    // printer is missing, the USB chooser pops so the cashier can re-pick it.
    async handleOpenDrawer() {
      this.openingDrawer = true;
      try {
        await openCashDrawer();
      } catch (e) {
        if (e && e.name === 'NotFoundError') return; // chooser dismissed
        this.$toast.error(this.$t('Open Drawer') + ': ' + (e.message || e));
      } finally {
        this.openingDrawer = false;
      }
    },
    websocketRequested(data) {
      playNotifySound(NOTIFY_SOUNDS.requested);
      const session = this.sessions.find(i => i.id === Number(data.id));
      if (session) {
        session.is_request = true;
      } else {
        this.fetchData();
      }
    },
    websocketBillRequested(data) {
      playNotifySound(NOTIFY_SOUNDS.checkBill);
      const session = this.sessions.find(i => i.id === Number(data.id));
      if (session) {
        session.is_bill_requested = true;
      } else {
        this.fetchData();
      }
    },
    websocketSessionOpen(data) {
      this.sessions.push(data);
      this.sessions.sort((a, b) => b.id - a.id);
    },
    websocketSessionClose(data) {
      // 1. Find the target item's index
      const index = this.sessions.findIndex(user => user.id === data.id);

      // 2. Safeguard and splice (remove 1 item at that index)
      if (index !== -1) {
        this.sessions.splice(index, 1);
      }
    },
    setParamsBeforeFetchData() {
      const params = _.cloneDeep(this.params);
      $.each(params, (key, value) => {
        if (value === '' || value === null || value === undefined) {
          delete params[key];
        }
      });
      return params;
    },
    async fetchData() {
      this.loading = true;
      const queryString = '?' + $.param(this.setParamsBeforeFetchData());
      await Sessions.list(queryString).then(res => {
        this.sessions = res.data.data || [];
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
        }
      });
      this.loading = false;
    },
    displayOrderNo(session) {
      return String(session.session_no || 0).padStart(4, '0');
    },
    typeIcon(type) {
      const t = this.sessionTypes.find(x => x.value === type);
      return t ? t.icon : 'fa-solid fa-receipt';
    },
    typeLabel(type) {
      const t = this.sessionTypes.find(x => x.value === type);
      return t ? t.label : type;
    },
    openNewSessionModal() {
      this.newSessionModal.open = true;
    },
    isPrintable(session) {
      return this.printableIdSet.has(session.id);
    },
    enterSelectMode() {
      if (!this.printableItems.length) return;
      this.selectMode = true;
      this.selectedIds = [];
    },
    exitSelectMode() {
      this.selectMode = false;
      this.selectedIds = [];
    },
    onCardClick(session) {
      if (this.selectMode) {
        if (!this.isPrintable(session)) return;
        const i = this.selectedIds.indexOf(session.id);
        if (i === -1) this.selectedIds.push(session.id);
        else this.selectedIds.splice(i, 1);
        return;
      }
      this.openSessionActions(session);
    },
    toggleAllQr() {
      this.selectedIds = this.allSelectedQr ? [] : this.printableItems.map(it => it.id);
    },
    async printSelectedQr() {
      const chosen = this.printableItems.filter(it => this.selectedIds.includes(it.id));
      if (!chosen.length) return;
      this.printing = true;
      this.printedCount = 0;
      try {
        const slips = chosen.map(it => ({
          heading: it.heading,
          subtitle: this.$t('Scan to Service'),
          url: serviceUrl(it.token),
        }));
        const res = await printQrSlips(slips, {
          onProgress: n => { this.printedCount = n; },
        });
        this.$toast.success(this.$t('Print QR Code') + ' ✓ (' + res.count + ')');
        this.exitSelectMode();
      } catch (e) {
        console.error('Bulk QR print error:', e);
        this.$toast.error(this.$t('Print failed') + ': ' + (e.message || e));
      } finally {
        this.printing = false;
      }
    },
    async closeSelected() {
      const tokens = this.printableItems
        .filter(it => this.selectedIds.includes(it.id))
        .map(it => it.token);
      if (!tokens.length) return;

      const confirm = await this.$swal({
        icon: 'warning',
        iconColor: '#FF7043',
        title: this.$t('End Service') + ' ' + tokens.length + ' ' + this.$t('items') + '?',
        html: `<div class="text-start mb-2">${this.$t('This will close the service session and release the table for new guests.')}</div>`
          + `<div class="small text-muted text-start"><i class="fa-solid fa-circle-info me-1"></i>${this.$t('Any unpaid bills will remain on file — settle them first if needed.')}</div>`,
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: this.$t('End Service'),
        buttonsStyling: false,
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary me-2' },
        reverseButtons: true
      });
      if (!confirm.isConfirmed) return;

      this.closing = true;
      try {
        const res = await Sessions.closeBatch({ tokens });
        const n = res?.data?.data?.count ?? tokens.length;
        this.$toast.success('<b>' + this.$t('Success') + '</b> ' + this.$t('End Service') + ' (' + n + ')');
        this.exitSelectMode();
        this.fetchData();
      } catch (e) {
        if (e?.response?.data?.message) this.$toast.warn(e.response.data.message);
        else this.$toast.error(this.$t('Close failed') + ': ' + (e.message || e));
      } finally {
        this.closing = false;
      }
    },
    openSessionActions(session) {
      this.actionsModal.session = session;
      this.actionsModal.open = true;
    },
    handleSessionCreated() {
      this.newSessionModal.open = false;
      this.fetchData();
    },
    handleOpenMenu() {
      this.activeSession = this.actionsModal.session;
      this.actionsModal.open = false;
      this.view = 'menu';
    },
    openCheckBill() {
      const s = this.actionsModal.session;
      if (!s) return;
      this.checkBill.name  = s.table_name || s.customer_name || ('#' + this.displayOrderNo(s));
      this.checkBill.token = s.token;
      this.checkBill.open  = true;
    },
    openMoveTable() {
      const s = this.actionsModal.session;
      if (!s || s.type !== 'dine_in') return;
      this.moveTable.token = s.token;
      this.moveTable.id    = s.table_id || 0;
      this.moveTable.name  = s.table_name || '';
      this.moveTable.total = s.total || 0;
      this.moveTable.open  = true;
    },
    openManageRequests() {
      const s = this.actionsModal.session;
      if (!s) return;
      this.serviceRequests.token = s.token;
      this.serviceRequests.name  = s.table_name || s.customer_name || ('#' + this.displayOrderNo(s));
      this.serviceRequests.open  = true;
    },
    onSessionMoved() {
      this.moveTable.open = false;
      this.actionsModal.open = false;
      this.fetchData();
    },
    openMergeSession() {
      const host = this.actionsModal.session;
      if (!host) return;
      // Candidates are fetched inside the offcanvas via the host token.
      this.mergeSession.hostToken = host.token;
      this.mergeSession.open = true;
    },
    onSessionsMerged() {
      this.mergeSession.open = false;
      this.actionsModal.open = false;
      this.fetchData();
    },
    onSessionClosed() {
      this.actionsModal.open = false;
      this.fetchData();
    },
    handleBackFromMenu() {
      this.view = 'list';
      this.activeSession = null;
      this.fetchData();
    },
    handleSessionUpdated(updatedSession) {
      const idx = this.sessions.findIndex(x => x.id === updatedSession.id);
      if (idx !== -1) {
        this.sessions.splice(idx, 1, { ...this.sessions[idx], ...updatedSession });
      }
    },
    handleSessionCharged(updatedSession) {
      this.view = 'list';
      this.activeSession = null;
      this.fetchData();
      this.$toast.success(
        '<b>' + this.$t('Charged') + ' #' + this.displayOrderNo(updatedSession) + '</b>'
      );
    }
  },
  beforeUnmount() {
    // this.stopPolling();
  }
};
</script>

<style scoped>
/* R2 card + action-bar styles อยู่ที่ custom.scss (global) — ใช้ร่วมกับหน้า
   platform-orders. ที่นี่เหลือเฉพาะโหมดเลือก (bulk actions) ที่ใช้แค่หน้านี้. */

/* ---- In-page bulk-actions selection mode --------------------------------- */
/* Selection = orange ring + full-card orange wash (no floating check badge —
   the card content is too packed for one to sit without overlapping). */
.qr-select-on {
  cursor: pointer;
  transition: box-shadow .12s ease, transform .12s ease;
}
.qr-selected {
  box-shadow: 0 0 0 2.5px #E96C15, 0 6px 16px rgba(233, 108, 21, .25);
  border-radius: 15px;
}
.qr-selected::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(233, 108, 21, .16);
  z-index: 2;
  pointer-events: none;
}
.qr-select-dim {
  opacity: .4;
  filter: grayscale(0.3);
  pointer-events: none;
}
</style>
